import React from 'react';
import { Home, TrendingUp, CreditCard, Landmark, ArrowUpCircle, DollarSign, Ban } from 'lucide-react';

export const ITEM_TYPES = {
  REAL_ESTATE: 'realEstate',
  OTHER_INVESTMENT: 'otherInvestment',
  EXPENSE: 'expense',
  LOAN: 'loan',
  INCOME: 'income',
};

export const ASSET_CLASSES = [
    { value: 'akcie', label: 'Akcie' },
    { value: 'dluhopisy', label: 'Dluhopisy' },
    { value: 'nemovitostni_fondy', label: 'Nemovitostní fondy/REITs' },
    { value: 'kryptomeny', label: 'Kryptoměny' },
    { value: 'komodity', label: 'Komodity' },
    { value: 'p2p', label: 'P2P Půjčky' },
    { value: 'ostatni_inv', label: 'Ostatní investice' },
];

// D4 — display currencies. Adding a code here is enough to reach every
// surface: both formatters in helpers.js, the Settings and onboarding pickers,
// the item form's symbol, and landingCurrency's allow-list all read this array
// and nothing else enumerates currencies.
//
// GBP is DISPLAY ONLY, like the rest of them. Pricing and billing are euros
// everywhere (see config/billing.js) regardless of what a portfolio is shown
// in, which is a deliberate choice — a UK visitor reads a sterling portfolio
// beside a €49 price.
export const SUPPORTED_CURRENCIES = [
    { code: 'CZK', symbol: 'Kč' },
    { code: 'USD', symbol: '$' },
    { code: 'EUR', symbol: '€' },
    { code: 'GBP', symbol: '£' },
    { code: 'CAD', symbol: 'C$' },
    { code: 'AUD', symbol: 'A$' },
    { code: 'CHF', symbol: 'CHF' },
    { code: 'INR', symbol: '₹' },
];

// Přidáno pro DEMO účely — divisors for the CZK-authored demo portfolio, so it
// reads plausibly in whichever currency it is displayed in. Approximate and
// hand-maintained on purpose: they decide nothing but the demo's numbers, and
// nothing here ever reaches Stripe.
export const DEMO_CURRENCY_CONVERSION_RATES = {
  'CZK': 1,
  'USD': 23.5,
  'EUR': 24.8,
  'GBP': 30,
  'CAD': 17,
  'AUD': 15.5,
  'CHF': 26.5,
  'INR': 0.27
};

export const ITEM_TYPE_OPTIONS = [
    { value: ITEM_TYPES.REAL_ESTATE, label: 'Nemovitost', icon: <Home/> },
    { value: ITEM_TYPES.OTHER_INVESTMENT, label: 'Jiná investice', icon: <TrendingUp/> },
    { value: ITEM_TYPES.INCOME, label: 'Příjem', icon: <ArrowUpCircle/> },
    { value: ITEM_TYPES.EXPENSE, label: 'Výdaj', icon: <CreditCard/> },
    { value: ITEM_TYPES.LOAN, label: 'Půjčka', icon: <Landmark/> },
];

export const getItemTypeIcon = (type, props = {}) => {
    const itemType = ITEM_TYPE_OPTIONS.find(opt => opt.value === type);
    if (props.isDisabled) return <Ban {...props} />;
    return itemType ? React.cloneElement(itemType.icon, props) : <DollarSign {...props}/>;
};

export const initialWidgetVisibilityConst = Object.freeze({
    liquidAssetsCard: true, portfolioValueCard: true, monthlyCashflowCard: true,
    annualAppreciationCard: true, equityRatioCard: true, totalIncomeCard: true,
    assetAllocationWidget: true, sankeyChartWidget: true, projectionChartWidget: true,
    fireSummaryCard: true,
});

// Single source of truth for the dashboardPreferences document shape.
// `onboardingStatus: 'completed'` is deliberate: a missing field must mean
// "legacy user, never show onboarding" — only the sign-up flow and the
// Settings replay/reset actions ever write 'pending'.
export const getDefaultDashboardSettings = () => ({
    widgetVisibility: { ...initialWidgetVisibilityConst },
    projectionInflationRate: 0.02,
    isProjectionInflationAdjusted: false,
    isScenarioActive: false,
    isProjectionGross: false,
    fireWithdrawalRate: 0.04,
    fireExpenseOverrideEnabled: false,
    fireExpenseOverrideAmount: 0,
    fireHorizonYears: 40,
    fireDrawdownEnabled: false,
    fireViewMode: 'fire',
    fireCoastTargetYear: new Date().getFullYear() + 25,
    onboardingStatus: 'completed',
    onboardingStep: 0,
    explanationsEnabled: true,
    dismissedExplanations: [],
    // F28 — how stale a value has to be before we estimate an updated one.
    // Mirrors DEFAULT_STALENESS_THRESHOLD in logic/review.js; kept as a
    // literal here because review.js imports this file (no import cycle).
    valueStalenessThreshold: 'month',
    // F5's other half: 'full' stacks the icon above the figure and keeps the
    // supporting line; 'compact' moves the icon beside the number and drops the
    // line, so six cards read in one glance.
    cardDensity: 'full',
});

// Values pre-filled or silently defaulted by the onboarding quick forms.
// Everything remains editable later through the full ItemForm.
export const ONBOARDING_DEFAULTS = Object.freeze({
    incomeGrowthRate: 0,
    expenseGrowthRate: 0,
    realEstateAppreciationRate: 0.03,
    investmentGrowthRates: { akcie: 0.07, dluhopisy: 0.03, ostatni_inv: 0.05 },
});

// F28 — every demo item carries a backdated confirmation anchor, spread so the
// portfolio review demonstrates each of its states at once: three old enough to
// carry an estimate, one confirmed recently enough not to (living costs, which
// only ever estimate after six months), the loan in the calculated drawer, and
// cash confirmed today in "Updated today". Deliberately no anchor-less item —
// almost nobody will have one, so demoing that state would misrepresent the
// page.
// N1 (2026-08-29) — the persona. The demo *is* the pitch, so the numbers in it
// decide who reads the landing page and thinks "that is not me". The first seed
// was a 120k/month IT consultant with a 7.5M flat and 1.2M in tech stocks —
// top-percentile Czech, and the outreach list this launch targets is
// beginner-ETF and general-audience. It now describes an ordinary EU household:
// ~60k Kc/month net (~EUR 2 400), a 5.5M flat carrying a 3.7M mortgage taken in
// 2021, a ~370k broad ETF with a 5 000/month standing order, ~200k cash, and
// ordinary living costs. All seven items and both inactive examples are kept,
// so the demo still exercises every item type, a mortgage mid-term, a
// near-repaid loan, the timeline events and the F28 anchors below.
//
// Change these and the landing page follows automatically — H5 made
// `useLandingDemoData` derive its figures from this file rather than keeping a
// second set. There is no second place to edit.

const demoMonthsAgo = (months) => new Date(Date.now() - months * 1000 * 60 * 60 * 24 * 30.4375);

export const demoItemsDataRaw = [
    {
        id: 'demo-realestate-1', includeInFire: true, isPassiveIncome: true, type: ITEM_TYPES.REAL_ESTATE, nameKey: 'demo.items.apartmentPrague', currentValue: 5500000,
        valueAsOf: demoMonthsAgo(8),
        annualAppreciationRate: 0.045, hasMortgage: true, mortgageInitialAmount: 3700000, mortgageTermTotalMonths: 300, 
        mortgageInterestRate: 0.028, mortgageMonthlyPayment: 0, mortgageStartDate: '2021-05-01', isRented: true, 
        monthlyRentalIncome: 19000, monthlyCosts: 0, amountFrequency: 'monthly', rentalAndCostsGrowthRate: 0.025, 
        isActive: true, userId: 'demo-user',
        isSimulationEnabled: true,
        sim_annualAppreciationRate_min: -0.04, sim_annualAppreciationRate_avg: 0.045, sim_annualAppreciationRate_max: 0.13,
        sim_rentalAndCostsGrowthRate_min: 0, sim_rentalAndCostsGrowthRate_avg: 0.025, sim_rentalAndCostsGrowthRate_max: 0.06,
    },
    { 
        id: 'demo-investment-1', includeInFire: true, isPassiveIncome: true, type: ITEM_TYPES.OTHER_INVESTMENT, nameKey: 'demo.items.etf', currentValue: 370000,
        valueAsOf: demoMonthsAgo(5),
        monthlyContribution: 5000, contributionFrequency: 'monthly', isGrowth: true, annualGrowthRate: 0.06, 
        isIncome: true, annualIncomeYield: 0.015, assetClass: 'akcie', isActive: true, userId: 'demo-user',
        isSimulationEnabled: true,
        sim_annualGrowthRate_min: -0.32, sim_annualGrowthRate_avg: 0.06, sim_annualGrowthRate_max: 0.38,
    },
    { 
        id: 'demo-income-1', isPassiveIncome: false, type: ITEM_TYPES.INCOME, nameKey: 'demo.items.mainIncome', monthlyIncome: 60000,
        valueAsOf: demoMonthsAgo(11),
        amountFrequency: 'monthly', annualGrowthRate: 0.05, isActive: true, userId: 'demo-user',
        isSimulationEnabled: true,
        sim_annualGrowthRate_min: -0.01, sim_annualGrowthRate_avg: 0.05, sim_annualGrowthRate_max: 0.11,
    },
    { 
        id: 'demo-expense-1', type: ITEM_TYPES.EXPENSE, nameKey: 'demo.items.livingCosts', monthlyExpense: 42000,
        valueAsOf: demoMonthsAgo(3),
        amountFrequency: 'monthly', annualGrowthRate: 0.03, isActive: true, userId: 'demo-user',
        isSimulationEnabled: false, // Příklad položky bez simulace
    },
    { 
        id: 'demo-loan-1', type: ITEM_TYPES.LOAN, nameKey: 'demo.items.studentLoan', loanAmount: 200000, 
        loanTermTotalMonths: 84, interestRate: 0.015, monthlyPayment: 0, startDate: '2019-09-01', isActive: true, 
        userId: 'demo-user' 
    },
    { 
        id: 'demo-realestate-2', type: ITEM_TYPES.REAL_ESTATE, nameKey: 'demo.items.cottage', currentValue: 1900000,
        valueAsOf: demoMonthsAgo(14),
        annualAppreciationRate: 0.03, hasMortgage: false, isRented: false, monthlyCosts: 2000, amountFrequency: 'monthly', 
        rentalAndCostsGrowthRate: 0.02, isActive: false, userId: 'demo-user', // Příklad neaktivní položky
        isSimulationEnabled: true,
        sim_annualAppreciationRate_min: -0.06, sim_annualAppreciationRate_avg: 0.03, sim_annualAppreciationRate_max: 0.12,
        sim_rentalAndCostsGrowthRate_min: 0, sim_rentalAndCostsGrowthRate_avg: 0.02, sim_rentalAndCostsGrowthRate_max: 0.05,
    },
    { 
        id: 'demo-investment-2', includeInFire: true, isPassiveIncome: true, type: ITEM_TYPES.OTHER_INVESTMENT, nameKey: 'demo.items.corporateBonds', currentValue: 180000,
        valueAsOf: demoMonthsAgo(2),
        monthlyContribution: 0, contributionFrequency: 'monthly', isGrowth: false, annualGrowthRate: 0, 
        isIncome: true, annualIncomeYield: 0.04, assetClass: 'dluhopisy', isActive: false, userId: 'demo-user'
    },
];

export const demoLiquidAssets = 200000;
export const demoLiquidAssetsInterestRate = 0.02;

// ---- the demo's snapshot history ------------------------------------------
//
// Two snapshots are seeded in demo mode so the app can show what it looks like
// with a past in it. Both used to carry today's portfolio verbatim, so
// "Include history" drew three points at the same height and the feature read
// as broken rather than as empty — annotation pass 4.
//
// Rather than a second table of numbers to keep in step with the first, every
// value is walked *backwards* along the rate the item already declares: a flat
// appreciating at 4.5% was worth value / 1.045 a year ago, a salary growing at
// 5% paid value / 1.05, living costs at 3% cost value / 1.03. Change a rate
// above and the history follows it.
//
// Two things are deliberately left alone. `mortgageInitialAmount` and
// `loanAmount` are the sums borrowed, not balances — the balance is worked out
// from the start date, so back-dating the principal would rewrite the loan
// rather than rewind it. And `monthlyContribution` is a standing order: the
// demo's saver has been paying in the same 5,000 all along.
// One entry per seeded snapshot, and everything that snapshot needs in the same
// object. `equityDrawdown` is the one factor equities get on top of their own
// rate: twelve months of a broad index is not a smooth 12% curve, and a history
// line that bends is the thing worth showing here — a drawdown a year ago and
// the recovery since is what makes the chart read as a record rather than as the
// projection drawn to the left of Now. It lived in a separate object keyed by
// the month, which meant changing a month here and forgetting it there silently
// dropped the drawdown for that snapshot; there is nothing to keep in step now.
export const DEMO_SNAPSHOTS = [
    { monthsAgo: 12, equityDrawdown: 0.86 },
    { monthsAgo: 6, equityDrawdown: 0.98 },
];

// Cash is the one balance no rate describes: the interest rate says what the
// deposit earns, not what its owner has been adding to it. A steady saver, in
// whatever currency the demo is being read in.
const DEMO_CASH_PER_MONTH = 0.976;

const backdate = (value, rate, monthsAgo) => (
    typeof value === 'number' && value !== 0
        ? value / Math.pow(1 + (rate || 0), monthsAgo / 12)
        : value
);

/** The demo portfolio as it stood at one of the `DEMO_SNAPSHOTS` dates. */
export const backdateDemoItems = (items, { monthsAgo, equityDrawdown = 1 }) => items.map(item => {
    const next = { ...item };
    const growth = item.annualGrowthRate;
    const rental = item.rentalAndCostsGrowthRate;

    if (typeof next.currentValue === 'number') {
        const rate = typeof item.annualAppreciationRate === 'number'
            ? item.annualAppreciationRate
            : (item.isGrowth ? growth : 0);
        next.currentValue = backdate(next.currentValue, rate, monthsAgo);
        if (item.assetClass === 'akcie') next.currentValue *= equityDrawdown;
    }
    if (typeof next.monthlyIncome === 'number') next.monthlyIncome = backdate(next.monthlyIncome, growth, monthsAgo);
    if (typeof next.monthlyExpense === 'number') next.monthlyExpense = backdate(next.monthlyExpense, growth, monthsAgo);
    if (typeof next.monthlyRentalIncome === 'number') next.monthlyRentalIncome = backdate(next.monthlyRentalIncome, rental, monthsAgo);
    if (typeof next.monthlyCosts === 'number') next.monthlyCosts = backdate(next.monthlyCosts, rental, monthsAgo);

    return next;
});

/** The demo's cash balance as it stood at one of those dates. */
export const backdateDemoLiquidAssets = (liquidAssets, { monthsAgo }) => (
    liquidAssets * Math.pow(DEMO_CASH_PER_MONTH, monthsAgo)
);

const futureDate = (years) => new Date(new Date().setFullYear(new Date().getFullYear() + years));

export const demoTimelineEventsData = [
    {
        id: 'demo-event-1',
        eventNameKey: 'demo.events.buyBrno',
        eventType: 'add',
        eventDate: futureDate(2),
        // Simulation bands on the timeline-added flat too. Without them this
        // item — roughly a third of the charted portfolio by year 10 — grew at
        // a flat 5 % in every single run, quietly anchoring the fan chart
        // whatever the other items did. A future the demo adds on purpose
        // should be as uncertain as the ones it starts with.
        itemData: { id: 'timeline-added-1', type: ITEM_TYPES.REAL_ESTATE, nameKey: 'demo.items.apartmentBrno', currentValue: 3600000, annualAppreciationRate: 0.05, hasMortgage: false, isRented: true, monthlyRentalIncome: 15000, amountFrequency: 'monthly', rentalAndCostsGrowthRate: 0.02,
            isSimulationEnabled: true,
            sim_annualAppreciationRate_min: -0.04, sim_annualAppreciationRate_avg: 0.05, sim_annualAppreciationRate_max: 0.14,
            sim_rentalAndCostsGrowthRate_min: 0, sim_rentalAndCostsGrowthRate_avg: 0.02, sim_rentalAndCostsGrowthRate_max: 0.05 }
    },
    {
        id: 'demo-event-2',
        eventNameKey: 'demo.events.salaryIncrease',
        eventType: 'update',
        targetItemId: 'demo-income-1',
        eventDate: futureDate(1),
        itemDataUpdate: { monthlyIncome: 70000 }
    },
    {
        id: 'demo-event-3',
        eventNameKey: 'demo.events.sellCottage',
        eventType: 'delete',
        targetItemId: 'demo-realestate-2',
        eventDate: futureDate(5),
    },
    {
        id: 'demo-event-4',
        eventNameKey: 'demo.events.mortgageRefinance',
        eventType: 'update',
        targetItemId: 'demo-realestate-1',
        eventDate: futureDate(3),
        itemDataUpdate: { mortgageInterestRate: 0.055 },
        isDisabled: true
    }
];